import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, InputNumber, Button, Tag, Popconfirm, Input } from 'antd';
import { SaveOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const money = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} $`;

/*
  Migration des créances (14/07) — onglet dédié des Paramètres, en 2 étapes simples :
  1. La DATE : les dettes enregistrées AVANT cette date disparaissent de tout le
     système (Centre des créances, rapports, alertes). Vide = tout est compté.
  2. Les CRÉANCES DE DÉPART : pour chaque cliente qui devait réellement de l'argent
     au moment de la migration, on saisit un solde propre — il vit ensuite sa vie
     normale (encaissement, échéances, relances, avoirs).
*/
export default function CreancesMigrationSettings({ items, onSaved }) {
  const dateSetting = items.find((s) => s.cle === 'creances_date_bascule');
  const [dateVal, setDateVal] = useState(dateSetting?.valeur ?? '');
  const [savingDate, setSavingDate] = useState(false);

  const [clients, setClients] = useState([]);
  const [ouvertures, setOuvertures] = useState([]);
  const [idClient, setIdClient] = useState(undefined);
  const [montant, setMontant] = useState(null);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    try {
      const [{ data: cl }, { data: ouv }] = await Promise.all([
        axios.get(`${DOMAIN}/api/client`),
        axios.get(`${DOMAIN}/api/creances-ouverture`),
      ]);
      setClients(Array.isArray(cl) ? cl : []);
      setOuvertures(Array.isArray(ouv?.ouvertures) ? ouv.ouvertures : []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Chargement impossible.');
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const saveDate = async () => {
    setSavingDate(true);
    try {
      await axios.put(`${DOMAIN}/api/parametre/settings/creances_date_bascule`, { valeur: dateVal || '' });
      onSaved && onSaved('creances_date_bascule', dateVal || '');
      toast.success(dateVal
        ? `Les créances antérieures au ${dateVal} sont désormais ignorées.`
        : 'Bascule désactivée : tout l’historique des créances est compté.');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Enregistrement refusé.');
    } finally { setSavingDate(false); }
  };

  const ajouter = async () => {
    if (busy) return;
    if (!idClient) { toast.warning('Choisissez une cliente.'); return; }
    if (montant == null || Number(montant) < 0) { toast.warning('Indiquez le montant dû (0 ou plus).'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/creances-ouverture`, { id_client: idClient, montant: Number(montant) });
      toast.success(data?.message || 'Enregistré.');
      setIdClient(undefined); setMontant(null);
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Enregistrement refusé.');
    } finally { setBusy(false); }
  };

  const supprimer = async (o) => {
    try {
      await axios.delete(`${DOMAIN}/api/creances-ouverture/${o.id_commande}`);
      toast.success('Créance de départ supprimée.');
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Suppression refusée.');
    }
  };

  const dejaOuvertes = useMemo(() => new Set(ouvertures.map((o) => Number(o.id_client))), [ouvertures]);
  const totalDepart = ouvertures.reduce((s, o) => s + Math.max(0, Number(o.montant_convenu) - Number(o.montant_paye)), 0);

  return (
    <div className="pb-group ndoe-card" style={{ display: 'grid', gap: 18, padding: 16 }}>
      {/* Étape 1 — la date de bascule */}
      <div>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#16304f' }}>1 · Repartir de zéro sur les créances</h3>
        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#6b7891' }}>
          Les dettes enregistrées <b>avant</b> cette date sont ignorées partout (Centre des créances,
          rapports, alertes). Laissez vide pour tout compter comme avant. Cette date ne supprime rien :
          vous pouvez la retirer à tout moment pour retrouver l’historique complet.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Input type="date" style={{ maxWidth: 200 }} value={dateVal} onChange={(e) => setDateVal(e.target.value)} />
          <Button type="primary" icon={<SaveOutlined />} loading={savingDate} onClick={saveDate}>Enregistrer</Button>
          {dateVal ? <Tag color="gold">Créances comptées à partir du {dateVal}</Tag> : <Tag>Désactivé — tout l’historique compte</Tag>}
        </div>
      </div>

      {/* Étape 2 — les créances de départ */}
      <div>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#16304f' }}>2 · Créances de départ par cliente</h3>
        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#6b7891' }}>
          Pour chaque cliente qui devait réellement de l’argent au moment de la migration, saisissez son
          solde de départ. Il apparaît au Centre des créances comme une dette normale : encaissable,
          avec échéance et relances. Re-saisir une cliente <b>met à jour</b> son montant — saisissez
          toujours le <b>solde de départ total</b> (jamais le « reste dû » : les paiements déjà
          encaissés se déduisent automatiquement).
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <Select
            showSearch allowClear optionFilterProp="label" placeholder="Choisir une cliente…"
            style={{ minWidth: 260 }} value={idClient} onChange={setIdClient}
            options={clients.map((c) => ({
              value: c.id,
              label: `${c.nom}${c.telephone ? ' · ' + c.telephone : ''}${dejaOuvertes.has(Number(c.id)) ? '  (déjà saisie)' : ''}`,
            }))}
          />
          <InputNumber inputMode="decimal" min={0} placeholder="Montant dû" style={{ width: 140 }} value={montant} onChange={setMontant} addonAfter="$" />
          <Button type="primary" icon={<PlusOutlined />} loading={busy} onClick={ajouter}>
            {idClient && dejaOuvertes.has(Number(idClient)) ? 'Mettre à jour' : 'Ajouter'}
          </Button>
        </div>

        {ouvertures.length === 0 ? (
          <p style={{ fontSize: 13, color: '#9aa3b2' }}>Aucune créance de départ saisie pour l’instant.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 560 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#7c8698', fontSize: 11.5 }}>
                  <th style={{ padding: '6px 10px 6px 0', borderBottom: '1px solid #eee8de' }}>Cliente</th>
                  <th style={{ padding: '6px 10px 6px 0', borderBottom: '1px solid #eee8de' }}>Solde de départ</th>
                  <th style={{ padding: '6px 10px 6px 0', borderBottom: '1px solid #eee8de' }}>Déjà payé</th>
                  <th style={{ padding: '6px 10px 6px 0', borderBottom: '1px solid #eee8de' }}>Reste dû</th>
                  <th style={{ padding: '6px 10px 6px 0', borderBottom: '1px solid #eee8de' }}>Échéance</th>
                  <th style={{ padding: '6px 0', borderBottom: '1px solid #eee8de' }} />
                </tr>
              </thead>
              <tbody>
                {ouvertures.map((o) => {
                  const reste = Math.max(0, Number(o.montant_convenu) - Number(o.montant_paye));
                  return (
                    <tr key={o.id_commande}>
                      <td style={{ padding: '7px 10px 7px 0', borderBottom: '1px solid #f4f0e7', fontWeight: 600, color: '#14243b' }}>{o.cliente || `#${o.id_client}`}</td>
                      <td style={{ padding: '7px 10px 7px 0', borderBottom: '1px solid #f4f0e7' }}>{money(o.montant_convenu)}</td>
                      <td style={{ padding: '7px 10px 7px 0', borderBottom: '1px solid #f4f0e7' }}>{money(o.montant_paye)}</td>
                      <td style={{ padding: '7px 10px 7px 0', borderBottom: '1px solid #f4f0e7' }}>
                        {reste > 0 ? <Tag color="gold">{money(reste)}</Tag> : <Tag color="green">Soldée</Tag>}
                      </td>
                      <td style={{ padding: '7px 10px 7px 0', borderBottom: '1px solid #f4f0e7' }}>{o.echeance || '—'}</td>
                      <td style={{ padding: '7px 0', borderBottom: '1px solid #f4f0e7', textAlign: 'right' }}>
                        <Popconfirm title="Supprimer cette créance de départ ?" okText="Supprimer" cancelText="Non"
                          onConfirm={() => supprimer(o)} disabled={Number(o.montant_paye) > 0}>
                          <Button size="small" danger icon={<DeleteOutlined />} disabled={Number(o.montant_paye) > 0}
                            title={Number(o.montant_paye) > 0 ? 'Des paiements existent : ajustez le montant plutôt que de supprimer.' : undefined} />
                        </Popconfirm>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ padding: '8px 10px 0 0', fontWeight: 700, color: '#14243b' }}>Total restant dû (départ)</td>
                  <td colSpan={5} style={{ padding: '8px 0 0', fontWeight: 700, color: '#a86f14' }}>{money(totalDepart)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
